// ----------------------------------------------------------------------
// <copyright file="Izenda.GaugeValueMetricItem.js" company="Izenda">
// Copyright (c) 2015 Izenda, Inc.  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and  International
// Copyright Laws. Unauthorized reproduction,  reverse-engineering, and
// distribution of all or any portion of  the code contained in this file is
// strictly prohibited and may  result in severe civil and criminal penalties
// and will be  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES  ARE CONFIDENTIAL AND
// PROPRIETARY TRADE  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
// LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET  CONTROLS AS
// PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED  FILES OR ANY
// PORTION OF ITS CONTENTS SHALL AT NO TIME BE  COPIED, TRANSFERRED, SOLD,
// DISTRIBUTED, OR OTHERWISE MADE  AVAILABLE TO OTHER INDIVIDUALS WITHOUT
// EXPRESS WRITTEN CONSENT  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON  ADDITIONAL
// RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * GaugeValue component
 */
import React, { Component, PropTypes } from 'react';
import connect from 'share/enhancers/Izenda.Connect';
import classnames from 'classnames';
import { get, getOr, concat, find, includes } from 'lodash/fp';

import EventsUtils from 'EventUtils';
import droppable from 'share/components/hoc/Izenda.Droppable';
import GaugeValueDetailsModel from 'logic/models/Izenda.ReportPartGaugeValueDetails';
import { ContainerName, MaximumField } from 'logic/data/Izenda.ReportPartGaugeConstants';
import { CHART_TYPE } from 'logic/data/Izenda.ChartDefaultConfig';
import InputByDataType from 'share/components/controls/Izenda.InputByDataType';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import GaugeValueSettings from './Izenda.GaugeValueSettings';
import ReportDesignerConstants from 'logic/data/Izenda.ReportDesignerConstants';
import ReportBodyConstants from 'logic/data/Izenda.ReportBodyConstants';
import { compose } from "recompose";
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import { action } from "mobx";

const GRID_SIZE_LABEL = 3;
const GRID_SIZE_CTRL = 9;
const GRID_LABEL = 'gauge-label control-label';
const GRID_CTRL = 'gauge-control';

function getScaleContainer(reportPart: ReportPartDetails) {
  const containers = get('reportPartContent.containerCollection', reportPart);
  return find({ name: ContainerName.SCALE }, containers);
}

const inputByDataTypeFieldDroppableAction = droppable(
  [ReportDesignerConstants.GENNERAL.FIELD_ITEM, ReportBodyConstants.DRAG_ITEM.REPORT_PART_FIELD_ITEM],
  {
    drop(props, monitor) {

      if (monitor.didDrop()) {
        return;
      }

      const item = monitor.getItem() || {};
      const querySourceField = item.querySourceField || {};
      const dragItemContainer = item.dragItemContainer;
      const reportPart = props.reportPart;
      const store = props.store;
      const querySource = item.dataObject || {};
      const container = getScaleContainer(reportPart);

      // Exit conditions
      if (item.itemType !== ReportDesignerConstants.GENNERAL.FIELD_ITEM
        && item.itemType !== ReportBodyConstants.DRAG_ITEM.REPORT_PART_FIELD_ITEM) {
        return;
      }
      if (querySourceField.isRunningField) {
        if (ReportPartUtils.isInvalidRunningField(reportPart, container, querySourceField.isRunningField)) {
          props.warning(String.format(ReportBodyConstants.MESSAGES.M28, querySourceField.name));
          return;
        }
      }
      if (dragItemContainer === container)
        return;
      if (dragItemContainer && dragItemContainer.name === ContainerName.VALUES)
        return;

      // add item
      const onDone = () => {
        reportPart.reportPartContent.afterAddField && reportPart.reportPartContent.afterAddField();
        reportPart.shouldUpdatePreview && store.needUpdatePreview();
      };
      if (dragItemContainer) {
        store.reportFilters.removeAggregatedField(querySourceField);
        item.onDeleteElement && item.onDeleteElement(querySourceField);
        reportPart
          .changeFieldContainer(querySourceField, 0, dragItemContainer, container)
          .then(addedField => {
            props.metricModel.scale[props.metricModelScaleKey] = addedField;
            props.onAddElement && props.onAddElement(addedField);
          })
          .then(onDone);
      } else if (!dragItemContainer) {
        reportPart
          .insertFieldToContainer(container, querySourceField, querySource, 0)
          .then(addedField => {
            props.metricModel.scale[props.metricModelScaleKey] = addedField;
            props.onAddElement && props.onAddElement(addedField);
          })
          .then(onDone);
      }
    },

    canAccess(props, currentDragItem) {
      const dragItem = currentDragItem || {};
      const item = dragItem.querySourceField || {};
      const isRunningField = get('isRunningField', item) || get('field.isRunningField', item);
      if (
        dragItem.itemType !== ReportDesignerConstants.GENNERAL.FIELD_ITEM &&
        dragItem.itemType !== ReportBodyConstants.DRAG_ITEM.REPORT_PART_FIELD_ITEM
      ) {
        return false;
      }

      if (isRunningField)
        return false;

      // If we're dragging from "VALUE" container - it's illegal.
      if (dragItem.dragItemContainer && (
        dragItem.dragItemContainer === props.container || dragItem.dragItemContainer.name === ContainerName.VALUES))
        return false;

      return true;
    }
  });

const InputByDataTypeWrapperDroppable = compose(inputByDataTypeFieldDroppableAction)(
  class InputByDataTypeWrapper extends Component {
    static propTypes = {
      onAddField: PropTypes.func.isRequired,
      metricModel: PropTypes.any.isRequired,
      metricModelScaleKey: PropTypes.string.isRequired,
      allowAccess: PropTypes.bool
    };

    render() {
      const { props } = this;
      const { children, isOver, allowAccess, item } = props;
      let isAccess = allowAccess && props.canAccess(props, item);
      const wrapperClasses = classnames({
        'with-radius': true,
        'can-drop': isOver && isAccess,
        'cannot-drop': isOver && !isAccess
      });
      return (
        <div className={wrapperClasses}>
          {children}
        </div>
      );
    }
  }
);

@connect()
export default class GaugeValueMetricItem extends Component {
  componentWillMount() {
    this.initMetricData(this.props.value);
  }

  componentWillReceiveProps(nextProps) {
    this.initMetricData(nextProps.value);
  }

  render() {
    const props = this.props;
    const localization = props.localization;
    const field = (this.field = props.value); // Field - main metric field
    const hasPrimaryField = !!field;
    const metricModel = this.metricModel;
    const fieldScaleFrom = getOr(null, 'scale.fromElement', metricModel);
    const fieldScaleTo = getOr(null, 'scale.toElement', metricModel);
    const hasScaleFrom = !!fieldScaleFrom;
    const hasScaleTo = !!fieldScaleTo;
    const reportPart = props.reportPart;
    const commonProps = {
      store: props.store,
      reportPart
    };

    return (
      <GridLayout className="gauge-item-value form-horizontal">
        {isSimpleGauge() && renderPrimaryKPI()}

        {renderValue.call(this)}
        {!isSimpleGauge() && renderScaleRange.call(this)}
        {renderUnitLabel.call(this)}
        {renderThreshold.call(this)}
        {isSimpleGauge() && renderSupplementary.call(this)}
      </GridLayout>
    );

    function isSimpleGauge() {
      return reportPart.reportPartContent.chartType === CHART_TYPE.SimpleGauge;
    }

    function renderPrimaryKPI() {
      return (
        <GridLayout.Row className="form-group">
          <GridLayout.Col cols={12}>
            <label className="control-label">{localization.getText('REPORTDESIGNER_LABEL_PRIMARY_KPI')}</label>
          </GridLayout.Col>
        </GridLayout.Row>
      );
    }

    function renderValue() {
      return (
        <GridLayout.Row className="form-group">
          <GridLayout.Col cols={GRID_SIZE_LABEL} className={GRID_LABEL}>
            {localization.getText('COMMON_LABEL_VALUE')}
          </GridLayout.Col>
          <GridLayout.Col cols={GRID_SIZE_CTRL} className={GRID_CTRL}>
            <GaugeValueSettings.FieldContainer
              {...commonProps}
              simpleViewForFullContainer={true}
              value={field}
              containerName={ContainerName.VALUES}
              onDelete={onDeleteField}
              onAdd={onAddField(props.index)}
            />
          </GridLayout.Col>
        </GridLayout.Row>
      );

      function onAddField(index) {
        return function () {
          props.onAddElement && props.onAddElement.apply(this, concat([index], arguments));
        };
      }

      function onDeleteField() {
        props.onDeleteElement && props.onDeleteElement(props.index, field);
      }

      function updatePreviewData() {
        return props.store.needUpdatePreview();
      }
    }

    /**
     * Render scale FROM-TO control
     */
    function renderScaleRange() {

      const onBlur = () => {
        props.validation.validate();
      };

      // value is 'from'/'to'
      const buildValidationConstraint = value => ({
        action() {
          return metricModel.validateThreshold.bind(metricModel, value);
        }
      });

      const validationsFrom = buildValidationConstraint('from');
      const validationsTo = buildValidationConstraint('to');

      const onAddScaleField = (scaleElementKey, field) => {
        // This below approach is required for re-rendering (Combination chart)
        metricModel.scale[scaleElementKey] = field;
        return Promise.resolve(field)
      };

      const onAddFromField = (field, querySource, position, preloadPromises) => {
        return onAddScaleField('fromElement', field, querySource, position, preloadPromises);
      };

      const onAddToField = (field, querySource, position, preloadPromises) => {
        return onAddScaleField('toElement', field, querySource, position, preloadPromises);
      };

      const onDeleteScaleField = scaleElementKey => scaleField => {
        metricModel.scale[scaleElementKey] = null;
        props.onDeleteElement && props.onDeleteElement(props.index, field, ContainerName.SCALE, scaleField);
      };

      return (
        <GridLayout.Row className="form-group">
          <GridLayout.Col cols={GRID_SIZE_LABEL} className={GRID_LABEL}>
            {localization.getText('REPORTDESIGNER_LABEL_SCALE')}
          </GridLayout.Col>
          {/*TEMP*/}
          <GridLayout.Col cols={GRID_SIZE_CTRL} className={classnames(GRID_CTRL, 'scale-range')}>
          </GridLayout.Col>
          {/*END TEMP*/}
          <GridLayout.Col cols={GRID_SIZE_CTRL} className={classnames(GRID_CTRL, 'scale-range')}>

            {/*SCALE FROM*/}
            {/*<span className="scale-from control-label ">{localization.getText('COMMON_LABEL_FROM')}</span>*/}
            {hasScaleFrom && (
              <GaugeValueSettings.FieldContainer
                {...commonProps}
                simpleViewForFullContainer={true}
                value={fieldScaleFrom}
                containerName={ContainerName.SCALE}
                onDelete={onDeleteScaleField('fromElement')}
              />
            )}

            {/*Wrapper which makes InputByDataType droppable*/}
            {!hasScaleFrom && (
              <InputByDataTypeWrapperDroppable
                metricModel={metricModel}
                metricModelScaleKey={'fromElement'}
                onAddField={onAddFromField}
                allowAccess={!hasScaleFrom}
                {...commonProps}>
                <InputByDataType
                  type="number"
                  disabled={!hasPrimaryField}
                  value={get('scale.from', metricModel)}
                  isDecimal
                  onChange={EventsUtils.notifyChange(this.changeMetricValue('scale.from'), EventsUtils.INPUT_BY_TYPE)}
                  onBlur={onBlur}
                  validations={validationsFrom}
                  field="from"
                />
              </InputByDataTypeWrapperDroppable>
            )}

            {/*SCALE TO*/}
            <span className="control-label "> - </span>
            {/*<span className="scale-to control-label ">{localization.getText('COMMON_LABEL_TO')}</span>*/}
            {hasScaleTo && (
              <GaugeValueSettings.FieldContainer
                {...commonProps}
                simpleViewForFullContainer={true}
                value={fieldScaleTo}
                containerName={ContainerName.SCALE}
                onDelete={onDeleteScaleField('toElement')}
              />
            )}
            {/*Wrapper which makes InputByDataType droppable*/}
            {!hasScaleTo && (
              <InputByDataTypeWrapperDroppable
                metricModel={metricModel}
                metricModelScaleKey={'toElement'}
                onAddField={onAddToField}
                allowAccess={!hasScaleTo}
                {...commonProps}>
                <InputByDataType
                  type="number"
                  disabled={!hasPrimaryField}
                  value={get('scale.to', metricModel)}
                  isDecimal
                  onChange={EventsUtils.notifyChange(this.changeMetricValue('scale.to'), EventsUtils.INPUT_BY_TYPE)}
                  onBlur={onBlur}
                  validations={validationsTo}
                  field="to"
                />
              </InputByDataTypeWrapperDroppable>
            )}

          </GridLayout.Col>
        </GridLayout.Row>
      );
    }

    function renderUnitLabel() {
      return (
        <GridLayout.Row className="form-group">
          <GridLayout.Col cols={GRID_SIZE_LABEL} className={classnames(GRID_LABEL, 'control-label')}>
            {localization.getText('REPORTDESIGNER_LABEL_UNIT_LABEL')}
          </GridLayout.Col>
          <GridLayout.Col cols={GRID_SIZE_CTRL} className={GRID_CTRL}>
            <InputByDataType
              type="text"
              disabled={!hasPrimaryField}
              value={metricModel.unitLabel}
              onChange={EventsUtils.notifyChange(this.changeMetricValue('unitLabel'), EventsUtils.INPUT_BY_TYPE)}
            />
          </GridLayout.Col>
        </GridLayout.Row>
      );
    }

    function renderThreshold() {
      return (
        <GaugeValueSettings.ThresholdSetting
          {...commonProps}
          disabled={!hasPrimaryField}
          thresholdSetting={metricModel.thresholds}
          metricModel={metricModel}
        />
      );
    }

    function renderSupplementary() {
      return (
        <GaugeValueSettings.SupplementarySetting
          {...commonProps}
          disabled={!hasPrimaryField}
          supplementaryKPI={metricModel.supplementaryKPI}
          metricModel={metricModel}
        />
      );
    }
  }

  changeMetricValue = changedKeyPath => {
    return newVal => {
      const metricModel = this.metricModel;
      metricModel.setDataByKey && metricModel.setDataByKey(changedKeyPath, newVal);
    };
  };

  initMetricData(field) {
    this.metricModel = this.getMetricData(field);
  }

  getMetricData(field, defaultVal) {
    return getOr(defaultVal || new GaugeValueDetailsModel(), 'properties.metric', field || {});
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/FieldContainers/Izenda.GaugeValueMetricItem.js